// app/src/features/submit/PreprocessHost.jsx
import React from "react";
import { applyResolutionMoment as cmdApplyResolutionMoment } from "../../commands/applyResolutionMoment.js";
import { makeDefaultDeps } from "../../commands/deps.js";
import { editResubmit as cmdEditResubmit } from "../../commands/editResubmit.js";
import PreprocessPanel from "../../components/PreprocessPanel.jsx";
import { PENDING_FORECAST_KEY } from "../../lib/forecast/storageKeys.js";

/**
 * The preprocess confirmation panel's host (last in the frame's `modals` slot): the panel for a
 * pending submission whose run is the active forecast or a branch beneath it, with its dismiss,
 * edit-and-resubmit and resolution-moment handlers bound to the submit commands.
 */
export default function PreprocessHost({ ctx }) {
  const {
    pendingPreprocess,
    active,
    branches,
    preprocessConfirmedRef,
    setPendingPreprocess,
    rerunCustomEventBranch,
    setForecastingPhase,
    compareMode,
    triggerLlm,
    forecastAbortRef,
    submitAbortRef,
    slideCache,
    autoZoom,
    doForecast,
    theme,
    bugOpen,
  } = ctx;
  return (
    <>
      {pendingPreprocess &&
        // The panel belongs to a SUBMISSION, and a branch is a submission too — its
        // own run, its own clientId, its own question ("If X happens, will Y?").
        // So the gate admits the active run OR any branch beneath it: a branch is
        // never itself `active` (the root is), and gating on `active.id` alone would
        // silently swallow the confirm panel for every branch.
        (active?.id === pendingPreprocess.clientId ||
          branches.some((b) => b.runId === pendingPreprocess.clientId)) &&
        (() => {
          const pp = pendingPreprocess;
          // Any dismiss path (CONTINUE, click-outside, auto-fill end, Enter key)
          // marks the current submission as "user-confirmed" — a later CLEAR on
          // this forecast will keep the log entry instead of discarding it.
          const dismiss = () => {
            preprocessConfirmedRef.current = true;
            // Stamp the reload handle so a later page reload doesn't re-pop a
            // confirmation the user already cleared. Best-effort: if the handle
            // hasn't been written yet (confirm raced ahead of traceAllocated),
            // the contract is still shown on resume — only the modal re-appears,
            // and it auto-continues in 15s. Keep `preprocessed` so the resumed
            // contract panel still renders from it.
            try {
              const raw = sessionStorage.getItem(PENDING_FORECAST_KEY);
              if (raw) {
                const h = JSON.parse(raw);
                if (h && h.clientId === pp.clientId) {
                  h.preprocessConfirmed = true;
                  sessionStorage.setItem(PENDING_FORECAST_KEY, JSON.stringify(h));
                }
              }
            } catch (_e) {
              /* private mode / quota / parse — non-fatal */
            }
            setPendingPreprocess(null);
          };
          const editResubmit = () => {
            // A typed TRIGGER re-runs its branch; a question re-runs the forecast. Same panel,
            // same button, different submission underneath — cmdEditResubmit is bound to the
            // top-level submit path (doForecast, the pending run, the forecast abort ref) and
            // would rebuild the ROOT forecast from the trigger's text.
            if (pp.branchTrigger) {
              dismiss();
              rerunCustomEventBranch(pp, {
                // A rejected correction leaves the edited branch standing: reopen its panel,
                // still editing the corrected text, with the reason — unless a newer one is open.
                onRejected: ({ message }) =>
                  setPendingPreprocess(
                    (current) =>
                      current || {
                        ...pp,
                        editing: true,
                        paused: true,
                        submitting: false,
                        submitError: message,
                      },
                  ),
              });
              return;
            }
            return cmdEditResubmit(
              {
                ...makeDefaultDeps(),
                ui: { setForecastingPhase, setPendingPreprocess, compareMode, triggerLlm },
                refs: { forecastAbortRef, submitAbortRef, slideCache },
                callbacks: { autoZoom, dismiss, doForecast },
              },
              { pp },
            );
          };
          // A picked resolution moment starts the HELD forecast, or reruns a running one.
          const chooseResolution = (choice) =>
            cmdApplyResolutionMoment(
              {
                ...makeDefaultDeps(),
                ui: { setForecastingPhase, setPendingPreprocess, compareMode, triggerLlm },
                refs: { forecastAbortRef, slideCache },
                callbacks: { autoZoom, dismiss, doForecast },
              },
              { pp, choice },
            );
          return (
            <PreprocessPanel
              pp={pp}
              theme={theme}
              bugOpen={bugOpen}
              updatePp={setPendingPreprocess}
              onDismiss={dismiss}
              onEditResubmit={editResubmit}
              onChooseResolution={chooseResolution}
            />
          );
        })()}
    </>
  );
}
